import React, { createContext, useState, useContext, useEffect } from "react";
import { authService } from "../services/authService";
import api from "../services/api";
import { setPermissions as setPermissionsStorage, getPermissions, clearPermissions } from "../utils/permissions";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState([]);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem("token");
    const savedUser = localStorage.getItem("user");

    if (token && savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        setUser(userData);
        // Load permissions from sessionStorage
        const savedPermissions = getPermissions();
        if (savedPermissions.length > 0) {
          setPermissions(savedPermissions);
        } else {
          // Fallback: load from API if not in sessionStorage
          loadUserPermissions(userData.roleId || userData.role_id);
        }
      } catch (error) {
        console.error("Error parsing user data:", error);
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        clearPermissions();
      }
    }
    setLoading(false);
  }, []);

  const loadUserPermissions = async (roleId) => {
    try {
      const response = await api.get(`/permissions/role-permissions/${roleId}`);
      const list = Array.isArray(response?.data?.data) ? response.data.data : [];
      const userPermissions = list
        .filter((p) => p.has_permission === 1)
        .map((p) => p.permission_key);
      setPermissionsStorage(userPermissions); // Store in sessionStorage
      setPermissions(userPermissions); // Update React state
    } catch (error) {
      console.error("Error loading permissions:", error);
    }
  };

  const login = async (type, credentials) => {
    try {
      let response;
      if (type === "superadmin") {
        response = await authService.superadminLogin(credentials);
      } else if (type === "admin") {
        response = await authService.adminLogin(credentials);
      } else {
        response = await authService.userLogin(credentials);
      }

      // Handle different response formats
      const responseData = response.data;
      
      // Check if MFA is required
      if (responseData.mfaRequired) {
        return {
          success: false,
          mfaRequired: true,
          message: responseData.message || "MFA code required",
          mfaCode: responseData.mfaCode, // Only in development
        };
      }

      const token = responseData.token;
      const userData = responseData.user || responseData.admin;
      const permissions = responseData.permissions || [];

      if (token && userData) {
        localStorage.setItem("token", token);
        localStorage.setItem("user", JSON.stringify(userData));
        setUser(userData);
        
        // Store permissions in sessionStorage
        if (permissions && permissions.length > 0) {
          setPermissionsStorage(permissions); // Store in sessionStorage
          setPermissions(permissions); // Update React state
        } else if (userData.role_id || userData.roleId) {
          // Fallback: load from API if not in response
          await loadUserPermissions(userData.role_id || userData.roleId);
        } else {
          // If no permissions and no role_id, check if superadmin/commissioner
          const userRole = userData.role?.toLowerCase() || "";
          if (userRole === "commissioner" || userRole === "superadmin") {
            // Superadmin has all permissions - store empty array, but hasPermission will return true
            setPermissionsStorage([]);
            setPermissions([]);
            console.log("Superadmin logged in - full access granted");
          }
        }
        
        return { success: true };
      } else {
        return {
          success: false,
          message: responseData.message || "Login failed",
        };
      }
    } catch (error) {
      const errorData = error.response?.data || {};
      return {
        success: false,
        message: errorData.message || errorData.error || "Login failed",
        actual: errorData.actual,
        expected: errorData.expected,
      };
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    clearPermissions();
    setUser(null);
    setPermissions([]);
  };

  const hasPermission = (permissionName) => {
    // Superadmin/Commissioner (role_id 1 or 6) has all permissions
    if (user) {
      const userRole = user.role?.toLowerCase() || "";
      const roleId = user.role_id;
      if (userRole === "superadmin" || userRole === "commissioner" || roleId === 1 || roleId === 6) {
        return true; // Superadmin has all permissions
      }
    }
    
    // Check both state and sessionStorage
    const sessionPerms = getPermissions();
    return permissions.includes(permissionName) || sessionPerms.includes(permissionName);
  };

  const value = {
    user,
    loading,
    permissions,
    login,
    logout,
    hasPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

